/**
 * CustomerServiceDashboardPage — `/customer-service` index.
 *
 * Live tiles from:
 *   - GET /v1/customers/lookup           — customer count
 *   - GET /v1/service-desk/cases         — open ticket count fallback
 *   - GET /v1/service-desk/stats         — open / breached SLA / avg resolution / CSAT
 *
 * The Stats endpoint replaces the previous "service-desk read surface
 * deferred" panel. Cards downgrade to a permission-required hint if the dev
 * token doesn't carry `ServiceDesk/readStats`.
 */
import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import { AlertOctagon, Clock, MessageSquare, Star, Ticket, UserX } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import { lookupCustomers } from '../../lib/api/customers';
import { getServiceDeskStats, listServiceCases } from '../../lib/api/service-desk';
import { PageHeader } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';

export function CustomerServiceDashboardPage(): JSX.Element {
  const customers = useQuery({
    queryKey: ['cs', 'customers', { pageSize: 1 }],
    queryFn: () => lookupCustomers({ pageSize: 1 }),
    retry: false,
    staleTime: 30_000,
  });
  const cases = useQuery({
    queryKey: ['cs', 'cases', { pageSize: 1 }],
    queryFn: () => listServiceCases({ pageSize: 1 }),
    retry: false,
    staleTime: 30_000,
  });
  const stats = useQuery({
    queryKey: ['cs', 'stats'],
    queryFn: () => getServiceDeskStats(),
    retry: false,
    staleTime: 30_000,
  });

  const customersDenied =
    customers.isError && customers.error instanceof ApiError && (customers.error.status === 401 || customers.error.status === 403);
  const casesDenied =
    cases.isError && cases.error instanceof ApiError && (cases.error.status === 401 || cases.error.status === 403);
  const statsDenied =
    stats.isError && stats.error instanceof ApiError && (stats.error.status === 401 || stats.error.status === 403);
  const statsUnavailable = stats.isError && !statsDenied;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Customer Service"
        description="Agent queue overview, lookup, complaints, escalations."
        actions={
          <>
            <Link
              to="/customer-service/lookup"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-border text-gray-700 hover:bg-gray-50"
            >
              <UserX className="w-4 h-4" /> Lookup
            </Link>
            <Link
              to="/customer-service/complaints"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-nipost-green text-white hover:bg-nipost-green/90"
            >
              <Ticket className="w-4 h-4" /> Complaints
            </Link>
          </>
        }
      />

      <div data-testid="cs-kpis" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        <KPICard
          title="Customers"
          value={customers.isPending ? '—' : customersDenied ? '—' : String(customers.data?.total ?? 0)}
          hint={customersDenied ? 'Permission required' : 'Total customer records'}
          icon={UserX}
          tone={customersDenied ? 'warning' : 'default'}
        />
        <KPICard
          title="Open tickets"
          value={
            stats.isSuccess
              ? String(stats.data.openCases)
              : cases.isPending
                ? '—'
                : casesDenied
                  ? '—'
                  : String(cases.data?.total ?? 0)
          }
          hint={
            statsDenied
              ? 'Permission required'
              : stats.isSuccess
                ? 'Open cases on the service desk'
                : 'Cases assigned to your team'
          }
          icon={Ticket}
          tone={casesDenied || statsDenied ? 'warning' : 'default'}
        />
        <KPICard
          title="Breached SLA"
          value={
            stats.isPending
              ? '—'
              : statsDenied || statsUnavailable
                ? '—'
                : String(stats.data?.breachedSla ?? 0)
          }
          hint={
            statsDenied
              ? 'Permission required'
              : statsUnavailable
                ? 'Stats endpoint unreachable'
                : 'Cases past their SLA window'
          }
          icon={AlertOctagon}
          tone={statsDenied || statsUnavailable ? 'warning' : 'default'}
        />
        <KPICard
          title="Avg resolution"
          value={
            stats.isPending
              ? '—'
              : statsDenied || statsUnavailable
                ? '—'
                : `${stats.data?.avgResolutionHours ?? 0}h`
          }
          hint={
            statsDenied
              ? 'Permission required'
              : statsUnavailable
                ? 'Stats endpoint unreachable'
                : 'avg(opened → resolved)'
          }
          icon={Clock}
          tone={statsDenied || statsUnavailable ? 'warning' : 'default'}
        />
        <KPICard
          title="CSAT"
          value={
            stats.isPending
              ? '—'
              : statsDenied || statsUnavailable
                ? '—'
                : `${stats.data?.csat ?? 0}%`
          }
          hint={
            statsDenied
              ? 'Permission required'
              : statsUnavailable
                ? 'Stats endpoint unreachable'
                : `${stats.data?.feedbackCount ?? 0} responses`
          }
          icon={Star}
          tone={statsDenied || statsUnavailable ? 'warning' : 'default'}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white border border-border rounded-xl p-5">
          <h3 className="font-semibold text-gray-900 text-sm mb-3 flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-nipost-green" /> Quick links
          </h3>
          <ul className="space-y-2 text-sm">
            <li><Link to="/customer-service/lookup" className="text-nipost-green hover:underline">Customer lookup</Link></li>
            <li><Link to="/customer-service/complaints" className="text-nipost-green hover:underline">Complaints queue</Link></li>
            <li><Link to="/customer-service/escalations" className="text-nipost-green hover:underline">Escalation management</Link></li>
            <li><Link to="/customer-service/segments" className="text-nipost-green hover:underline">Customer segments</Link></li>
            <li><Link to="/customer-service/feedback" className="text-nipost-green hover:underline">Feedback inbox</Link></li>
            <li><Link to="/customer-service/track" className="text-nipost-green hover:underline">Public tracking lookup</Link></li>
          </ul>
        </div>
      </div>
    </div>
  );
}
